<template>
  <div class="infinite-list-wrapper" :style="{ height: containerHeight }">
    <ul
      v-infinite-scroll="load"
      :infinite-scroll-disabled="disabled"
      :infinite-scroll-distance="10"
      :infinite-scroll-immediate="true"
      :style="{ height: listHeight}"
      class="infinite-list"
    >
      <li v-for="(item, index) in listData" :key="index" class="infinite-list-item">
        <div class="file-info">
          <el-tooltip
            v-if="item.fileName"
            placement="top"
            effect="light"
            popper-class="multi-line-tooltip"
            :content="item.fileName"
          >
            <div class="file-name">{{ item.fileName }}</div>
          </el-tooltip>
          <div v-else class="file-name">-</div>
          <div class="file-status">
            <div v-if="item.status === 'SUCCESS'" class="status-success">
              <i class="el-icon-circle-check" /> {{ $tr('taskPane.success', 'Success') }}
            </div>
            <div v-else-if="item.status === 'FAILURE'" class="status-failure">
              <i class="el-icon-circle-close" /> {{ $tr('taskPane.failure', 'Failure') }}
            </div>
            <span v-else class="status-default">
              {{ item.status }}
            </span>
            <el-tooltip
              v-if="item.status === 'FAILURE' && item.errorReason"
              placement="top"
              effect="light"
              popper-class="multi-line-tooltip"
            >
              <span slot="content" v-html="item.errorReason" />
              <div class="status-failure error-reason">
                {{ item.errorReason }}
              </div>
            </el-tooltip>
          </div>
          <div v-if="item.finishTime" class="file-time">{{ item.finishTime }}</div>
        </div>
        <div class="file-actions">
          <el-button
            type="primary"
            size="mini"
            icon="el-icon-download"
            circle
            :disabled="!!!item.ossFileUrl"
            :class="item.ossFileUrl ? 'white-icon-btn' : 'white-icon-btn-disabled'"
            @click="handleDownload(item)"
          />
        </div>
      </li>
      <li v-if="loading" class="loading-more">{{ $tr('taskPane.loading', 'Loading...') }}</li>
      <li v-if="noMore" class="no-more">{{ $tr('taskPane.noMore', 'No more data') }}</li>
    </ul>
  </div>
</template>

<script>
import { getTaskList, getFilePath } from '@/api'
import moment from 'moment'

export default {
  name: 'TaskTabPane',
  props: {
    taskType: {
      type: String,
      default: 'DOWNLOAD'
    }
  },
  data() {
    return {
      listData: [],
      loading: false,
      pageNum: 1,
      pageSize: 10,
      total: 0,
      hasMore: true,
      containerHeight: 'calc(100vh - 80px)',
      listHeight: '100%'
    }
  },
  computed: {
    noMore() {
      return !this.hasMore || this.listData.length >= this.total
    },
    disabled() {
      return this.loading || this.noMore
    }
  },
  mounted() {
    this.calculateHeight()
    window.addEventListener('resize', this.calculateHeight)
    // 不在 mounted 时加载数据，由父组件控制刷新
    // this.loadInitialData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.calculateHeight)
  },
  methods: {
    refreshData() {
      // 每次打开弹框时刷新数据
      this.loadInitialData()
    },
    handleDownload(item) {
      // 这里可以添加下载逻辑
      const params = {
        ossUrl: item.ossFileUrl
      }
      getFilePath(params).then(res => {
        if (res.success) {
          window.open(res.data)
        }
      })
    },
    calculateHeight() {
      // 精确计算可用高度
      const navbarHeight = 50 // Navbar高度
      const padding = 30 // 内边距和其他元素
      const availableHeight = window.innerHeight - navbarHeight - padding
      this.containerHeight = `${Math.max(availableHeight, 300)}px`
      this.listHeight = '100%'
    },
    async load() {
      if (this.disabled) return

      this.loading = true
      try {
        await this.loadMoreData()
      } finally {
        this.loading = false
      }
    },
    async loadInitialData() {
      this.pageNum = 1
      this.listData = []
      this.hasMore = true
      await this.loadMoreData()
    },
    async loadMoreData() {
      try {
        const params = {
          taskType: this.taskType,
          pageNum: this.pageNum,
          PageSize: 20
        }
        const res = await getTaskList(params)
        if (res.success) {
          let newData = []
          if (res.data.list && res.data.list.length) {
            newData = res.data.list.map(item => {
              item.finishTime = item.finishTime ? moment(item.finishTime).format('YYYY-MM-DD HH:mm:ss') : ''
              return item
            })
          }
          this.total = Number(res.data.total) || 0

          if (this.pageNum === 1) {
            this.listData = newData
          } else {
            this.listData = [...this.listData, ...newData]
          }

          // 判断是否还有更多数据
          this.hasMore = this.listData.length < this.total
          this.pageNum++
        }
      } catch (error) {
        this.$message.error(error.message)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.infinite-list-wrapper {
  min-height: 300px;
  overflow: hidden;
  border: none;
  border-radius: 0;

  .infinite-list {
    height: 100%;
    min-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0;
    margin: 0;
    list-style: none;

    // 滚动条样式优化
    &::-webkit-scrollbar {
      width: 4px;
    }
    &::-webkit-scrollbar-track {
      background: transparent;
    }
    &::-webkit-scrollbar-thumb {
      background: #c0c4cc;
      border-radius: 2px;
      &:hover {
        background: #a8abb2;
      }
    }
    // Firefox滚动条样式
    scrollbar-width: thin;
    scrollbar-color: #c0c4cc #f5f5f5;
  }

  .infinite-list-item {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    background: #fff;
    border-radius: 4px;
    margin-bottom: 10px;
    border-bottom: 1px solid #f0f0f0;
    transition: all 0.2s ease;
    cursor: pointer;

    &:last-child {
      border-bottom: none;
    }
    .file-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-width: 0; // 防止文本溢出
      margin-right: 20px;
      .file-name {
        font-size: 14px;
        font-weight: 500;
        color: black;
        line-height: 25px;
        word-break: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .file-time {
        font-size: 12px;
        color: #666;
        line-height: 25px;
        word-break: break-word;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .file-status {
        line-height: 25px;
        font-size: 12px;
        .status-success {
          i {
            color: green;
            margin-right: 6px;
            font-size: 18px;
            position: relative;
            top: 2px;
          }
        }
        .status-failure {
          i {
            color: red;
            margin-right: 6px;
            font-size: 18px;
            position: relative;
            top: 2px;
          }
        }

        .error-reason {
          display: inline-block;
          max-width: 300px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          vertical-align: bottom;
          color: #666;
        }
      }
    }
    .file-actions {
      display: flex;
      align-items: center;
      margin-left: 16px;
      flex-shrink: 0;
      .el-button {
        padding: 6px;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        transition: all 0.2s;
        i {
          font-size: 18px;
        }
      }

      .white-icon-btn {
        background: #163dc9;
        border-color: #163dc9;

        &:hover {
          background: #163dc9;
          border-color: #163dc9;
        }

        i {
          color: white !important;
        }
      }

      .white-icon-btn-disabled {
        background: #666;
        border-color: #666;

        &:hover {
          background: #666;
          border-color: #666;
        }

        i {
          color: white !important;
        }
      }
    }
  }

  .loading-more {
    text-align: center;
    padding: 24px;
    color: #909399;
    font-size: 14px;
    background: #fafafa;
  }

  .no-more {
    text-align: center;
    color: #c0c4cc;
    font-size: 14px;
    border-top: 1px solid #f0f0f0;
  }
}

// 响应式优化
@media screen and (max-width: 768px) {
  .infinite-list-wrapper {
    .infinite-list-item {
      padding: 10px 12px;
      margin: 0 8px;

      .file-info {
        .file-name {
          font-size: 14px;
        }

        .file-status {
          font-size: 12px;
        }
      }

      .file-actions {
        margin-left: 12px;

        .el-button {
          width: 32px;
          height: 32px;
          padding: 6px;

          i {
            font-size: 16px;
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.multi-line-tooltip {
  max-width: 480px;
  white-space: normal;
  word-break: break-all;
}
</style>
